export interface InterpolatablePlayerState {
  x: number;
  y: number;
  rotation: number;
  points?: { x: number; y: number; angle: number }[];
}

interface Snapshot<T extends InterpolatablePlayerState> {
  timeMs: number;
  state: T;
}

export class InterpolationBuffer<T extends InterpolatablePlayerState> {
  private readonly snapshotsByEntity: Map<string, Snapshot<T>[]> = new Map();
  private readonly interpolationDelayMs: number;
  private readonly maxBufferAgeMs: number;

  constructor(interpolationDelayMs: number = 120, maxBufferAgeMs: number = 2000) {
    this.interpolationDelayMs = interpolationDelayMs;
    this.maxBufferAgeMs = maxBufferAgeMs;
  }

  push(entityId: string, timeMs: number, state: T): void {
    const existing = this.snapshotsByEntity.get(entityId) ?? [];
    existing.push({ timeMs, state });
    existing.sort((a, b) => a.timeMs - b.timeMs);

    const minTime = timeMs - this.maxBufferAgeMs;
    while (existing.length > 0 && existing[0].timeMs < minTime) {
      existing.shift();
    }

    this.snapshotsByEntity.set(entityId, existing);
  }

  sample(entityId: string, renderNowMs: number): T | null {
    const snapshots = this.snapshotsByEntity.get(entityId);
    if (!snapshots || snapshots.length === 0) {
      return null;
    }

    const targetTime = renderNowMs - this.interpolationDelayMs;
    if (snapshots.length === 1) {
      return snapshots[0].state;
    }

    let older = snapshots[0];
    let newer = snapshots[snapshots.length - 1];

    for (let i = 0; i < snapshots.length - 1; i++) {
      const current = snapshots[i];
      const next = snapshots[i + 1];
      if (targetTime >= current.timeMs && targetTime <= next.timeMs) {
        older = current;
        newer = next;
        break;
      }
      if (targetTime > next.timeMs) {
        older = next;
        newer = next;
      }
    }

    if (older.timeMs === newer.timeMs) {
      return newer.state;
    }

    const alpha = Math.max(0, Math.min(1, (targetTime - older.timeMs) / (newer.timeMs - older.timeMs)));
    return {
      ...newer.state,
      x: older.state.x + (newer.state.x - older.state.x) * alpha,
      y: older.state.y + (newer.state.y - older.state.y) * alpha,
      rotation: older.state.rotation + (newer.state.rotation - older.state.rotation) * alpha
    };
  }

  clear(entityId: string): void {
    this.snapshotsByEntity.delete(entityId);
  }
}
